import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import ApplicationCard from '../components/ApplicationCard';

const statusLabels = {
  '': 'Все статусы',
  'pending': 'Ожидает',
  'active': 'В работе',
  'completed': 'Завершена',
  'rejected': 'Отклонена'
};

const themeLabels = {
  '': 'Все темы',
  'moto': 'Мототехника',
  'delivery': 'Доставка',
  'payment': 'Оплата',
  'undefined': 'Без темы'
};

function Applications() {
  const [applications, setApplications] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    theme: '',
    manager: '',
    sort: 'created_at'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    active: 0,
    completed: 0
  });

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getApplications(filters);
      setApplications(data);
      
      const newStats = {
        total: data.length,
        pending: data.filter(app => app.status === 'pending').length,
        active: data.filter(app => app.status === 'active').length,
        completed: data.filter(app => app.status === 'completed').length
      };
      setStats(newStats);
    } catch (error) {
      toast.error('Ошибка загрузки заявок');
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchManagers = useCallback(async () => {
    try {
      const data = await api.getManagers();
      setManagers(data);
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    fetchManagers();
  }, [fetchManagers]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      theme: '',
      manager: '',
      sort: 'created_at'
    });
  };

  const hasActiveFilters = filters.search || filters.status || filters.theme || filters.manager;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Заявки</h2>
          <div className="stats-summary">
            <span>Всего: <strong>{stats.total}</strong></span>
            <span className="stat-pending">Ожидают: <strong>{stats.pending}</strong></span>
            <span className="stat-active">В работе: <strong>{stats.active}</strong></span>
            <span className="stat-completed">Завершено: <strong>{stats.completed}</strong></span>
          </div>
        </div>
        <div className="action-buttons">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${showFilters || hasActiveFilters ? 'btn-primary' : 'btn-outline'}`}
          >
            🔍 Фильтры
          </button>
          <button 
            onClick={fetchApplications}
            className="btn btn-outline"
            disabled={loading}
          >
            🔄 Обновить
          </button>
        </div>
      </div>

      {(showFilters || hasActiveFilters) && (
        <div className="page-filters">
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Поиск по заявкам..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="filters-row">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={filters.theme}
              onChange={(e) => handleFilterChange('theme', e.target.value)}
            >
              {Object.entries(themeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={filters.manager}
              onChange={(e) => handleFilterChange('manager', e.target.value)}
            >
              <option value="">Все менеджеры</option>
              {managers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.name}
                </option>
              ))}
            </select>

            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
            >
              <option value="created_at">По дате создания</option>
              <option value="updated_at">По дате обновления</option>
              <option value="status">По статусу</option>
              <option value="theme">По теме</option>
            </select>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="btn btn-outline"
              >
                Сбросить
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="loading-card">
              <div className="loading-line loading-title"></div>
              <div className="loading-line loading-text"></div>
              <div className="loading-line loading-text"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="applications-list">
          {applications.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>
                {hasActiveFilters ? 'Заявки не найдены' : 'Заявок пока нет'}
              </h3>
              <p>
                {hasActiveFilters 
                  ? 'Попробуйте изменить фильтры поиска'
                  : 'Заявки будут отображаться здесь после их создания'
                }
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="btn btn-primary"
                >
                  Сбросить фильтры
                </button>
              )}
            </div>
          ) : (
            <>
              {applications.map((application) => (
                <ApplicationCard
                  key={application.id}
                  application={application}
                  managers={managers}
                  onUpdate={fetchApplications}
                />
              ))}
              <div className="results-summary">
                Показано {applications.length} заявок
                {hasActiveFilters && <span> (с учетом фильтров)</span>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Applications;